/**
 * API Endpoints
 * Centralized list of all API routes to avoid hardcoded URLs.
 */

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: "/auth/login",
    REGISTER: "/auth/register",
    LOGOUT: "/auth/logout",
    CHANGE_PASSWORD: "/auth/change-password"
  },
  TOUR_TYPES: {
    GET_ADMIN: "/tour-types/admin",
    CREATE: "/tour-types",
    UPDATE: (id) => `/tour-types/${id}`,
    DELETE: (id) => `/tour-types/${id}`
  },
  PACKAGES: {
    GET_ALL: "/packages",
    GET_BY_ID: (id) => `/packages/${id}`,
    CREATE: "/packages",
    DELETE: (id) => `/packages/${id}`,
    UPDATE: (id) => `/packages/${id}`,
    TOGGLE_STATUS: (id) => `/packages/${id}/toggle-status`,
    UPDATE_IMAGE: (id) => `/packages/${id}/image`,
    GET_FAQS: (id) => `/packages/${id}/faqs`,
    ADD_FAQ: (id) => `/packages/${id}/faqs`,
    UPDATE_FAQ: (faqId) => `/packages/faqs/${faqId}`,
    DELETE_FAQ: (faqId) => `/packages/faqs/${faqId}`,
    GET_FEATURES: (id) => `/packages/${id}/features`,
    UPDATE_INCLUDES: (id) => `/packages/${id}/includes`,
    UPDATE_EXCLUDES: (id) => `/packages/${id}/excludes`,
    UPDATE_TERMS: (id) => `/packages/${id}/terms`,
    UPDATE_WHY_CHOOSE_US: (id) => `/packages/${id}/why-choose-us`,
    UPDATE_TOUR_HIGHLIGHTS: (id) => `/packages/${id}/tour-highlights`,
    UPDATE_TOUR_BENEFITS: (id) => `/packages/${id}/tour-benefits`,
    UPDATE_WHEN: (id) => `/packages/${id}/when`,
    GET_GALLERY: (id) => `/packages/${id}/gallery`,
    UPLOAD_GALLERY: (id) => `/packages/${id}/gallery`,
    DELETE_GALLERY: (imageId) => `/packages/gallery/${imageId}`,
    GET_ITINERARY: (id) => `/packages/${id}/itinerary`,
    ADD_ITINERARY: (id) => `/packages/${id}/itinerary`,
    UPDATE_ITINERARY: (itineraryId) => `/packages/itinerary/${itineraryId}`,
    DELETE_ITINERARY: (itineraryId) => `/packages/itinerary/${itineraryId}`,
    GET_PRICING: (id) => `/packages/${id}/pricing`,
    ADD_PRICING: (id) => `/packages/${id}/pricing`,
    UPDATE_PRICING: (pricingId) => `/packages/pricing/${pricingId}`,
    DELETE_PRICING: (pricingId) => `/packages/pricing/${pricingId}`,
    GET_SCHEDULE: (id) => `/packages/${id}/schedule`,
    UPDATE_SCHEDULE: (id) => `/packages/${id}/schedule`
  },
  ITINERARY: {
    GET_BY_PACKAGE: (packageId) => `/itinerary/package/${packageId}`,
    GET_BY_ID: (id) => `/itinerary/${id}`,
    UPSERT_MULTI_DAY: "/itinerary/multi-day"
  },
  PRIVATE_TRANSFERS: {
    GET_ALL_ADMIN: "/private-transfers/admin/all",
    GET_BY_ID: (id) => `/private-transfers/${id}`,
    CREATE: "/private-transfers",
    UPDATE: (id) => `/private-transfers/${id}`,
    DELETE: (id) => `/private-transfers/${id}`,
  },
  BOOKINGS: {
    GET_ALL: "/bookings",
    GET_BY_ID: (id) => `/bookings/${id}`,
    DELETE: (id) => `/bookings/${id}`,
  },
  TRANSFER_BOOKINGS: {
    GET_ALL: "/private-transfers/admin/bookings",
    GET_BY_ID: (id) => `/transfer/booking/${id}`,
    DELETE: (id) => `/private-transfers/admin/bookings/${id}`,
  },
  USERS: {
    GET_ALL: "/users",
    PROFILE: "/users/profile",
    UPDATE_PROFILE: "/users/profile"
  },
  ENQUIRIES: {
    GET_ALL: "/enquiries",
    CLOSE: (id) => `/enquiries/${id}/close`,
    DELETE: (id) => `/enquiries/${id}`,
  },
  COUPONS: {
    GET_ALL: "/coupons",
    CREATE: "/coupons",
    UPDATE: (id) => `/coupons/${id}`,
    TOGGLE_STATUS: (id) => `/coupons/${id}/toggle`, // PATCH matches curl
    TOGGLE_POPUP: (id) => `/coupons/${id}/toggle-popup`, // PATCH for popup toggle
    DELETE: (id) => `/coupons/${id}` // DELETE matches curl
  },
  INSTAGRAM: {
    GET_ALL: "/instagram",
    GET_BY_ID: (id) => `/instagram/${id}`,
    CREATE: "/instagram",
    UPDATE: (id) => `/instagram/${id}`,
    DELETE: (id) => `/instagram/${id}`
  },
  BLOGS: {
    GET_ALL: "/blogs",
    GET_ALL_ADMIN: "/blogs/admin/all",
    GET_BY_ID: (id) => `/blogs/${id}`,
    CREATE: "/blogs",
    UPDATE: (id) => `/blogs/${id}`,
    DELETE: (id) => `/blogs/${id}`
  },
  YOUTUBE: {
    GET_ALL: "/youtube",
    GET_BY_ID: (id) => `/youtube/${id}`,
    CREATE: "/youtube",
    UPDATE: (id) => `/youtube/${id}`,
    DELETE: (id) => `/youtube/${id}`
  },
  POLICIES: {
    GET_LATEST: "/policies?latest=true",
    UPDATE: (id) => `/policies/${id}`
  },
  TERMS: {
    GET_LATEST: "/terms?latest=true",
    UPDATE: (id) => `/terms/${id}`
  },
  PRIVACY: {
    GET_LATEST: "/privacy?latest=true",
    UPDATE: (id) => `/privacy/${id}`
  },
  WEBSITE_GALLERY: {
    GET_ALL: "/gallery",
    ADD: "/gallery",
    UPDATE: (id) => `/gallery/${id}`,
    DELETE: (id) => `/gallery/${id}`
  },
  EMAIL_TEMPLATES: {
    GET_ALL: "/email-templates",
    CREATE: "/email-templates",
    UPDATE: (id) => `/email-templates/${id}`,
    UPDATE_BY_NAME: (name) => `/email-templates/${name}`
  },
  HOME_VIDEOS: {
    GET_ALL: "/home-videos",
    CREATE: "/home-videos",
    UPDATE: (id) => `/home-videos/${id}`,
    DELETE: (id) => `/home-videos/${id}`
  },
  HERO_VIDEOS: {
    GET: "/config/hero-video",
    UPLOAD_WINTER: "/config/hero-video/winter",
    UPLOAD_SUMMER: "/config/hero-video/summer",
    UPDATE: "/config/hero-video"
  },
  POPULAR_TOURS: {
    GET_ALL: "/homepage-popular-tours",
    CREATE: "/homepage-popular-tours",
    UPDATE: (id) => `/homepage-popular-tours/${id}`,
    DELETE: (id) => `/homepage-popular-tours/${id}`
  },
  FAQS: {
    GET_ALL: "/faqs",
    GET_BY_IDENTIFIER: (identifier) => `/faqs/${identifier}`,
    CREATE: "/faqs",
    UPDATE: (identifier) => `/faqs/${identifier}`,
    DELETE: (identifier) => `/faqs/${identifier}`,
    SEED: "/faqs/seed",
    ADD_QUESTION: (identifier) => `/faqs/${identifier}/questions`,
    UPDATE_QUESTION: (identifier, questionId) => `/faqs/${identifier}/questions/${questionId}`,
    DELETE_QUESTION: (identifier, questionId) => `/faqs/${identifier}/questions/${questionId}`
  },
  SEO: {
    GET_ALL: "/seo",
    CREATE: "/seo",
    UPDATE: (id) => `/seo/${id}`,
    DELETE: (id) => `/seo/${id}`
  }
};
